import React, { useEffect, useState } from "react";
import { Autocomplete, Box, Button, ListItem, MenuItem, TextField } from "@mui/material";
import { Modal } from "antd";
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from "date-fns";
import { DataGrid } from "@mui/x-data-grid";
import { assignCompetencyV2ToUser, deleteUserCompetency, getAllCompetenciesV2, getUserCompetencies, removeTicketFromUser } from "../../../../api/apiCall";
import { useAuth } from "../../../../auth/auth";
import dayjs from 'dayjs';
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { getStatus } from "../../../../utils/statusUtils";
import EditCompetency from "./EditCompetency/EditCompetency";

const Competencies = ({profileInfo, getWorkerInfo}: any) => {
    const {state} = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [competencies, setCompetencies] = useState<any>([]);
    const [isModalCompetencyOpen, setIsModalCompetencyOpen] = useState(false);
    const [isModalEditCompetencyOpen, setIsModalEditCompetencyOpen] = useState(false);
    const [allCompetencies, setAllCompetencies] = useState<any>([]);
    const [selectedCompetency, setSelectedCompetency] = useState<any>();
    const [image, setImage] = useState<any>(null);
    const [expDate, setExpDate] = useState<any>(dayjs());
    const [status, setStatus] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [id, setId] = useState<any>('');
    const [, setOpen] = useState(false);

    useEffect(() => {
        getAllCompetenciesV2(state.user?.token, false).then(setAllCompetencies);
    }, []);

    const handleCancelCompetency = () => {
        setIsModalCompetencyOpen(false);
    };

    const handleCancelEditCompetencyModal = () => {
        setSelectedCompetency(null);
        setIsModalEditCompetencyOpen(false);
    };

    const handleImageUpload = (event: any) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const fileArray = Array.from(files);
            setImage(fileArray);
        }
    };

    const addCompetency = async () => {
        const response = await assignCompetencyV2ToUser(state.user?.token, {
            userId: profileInfo.id,
            competencyId: selectedCompetency.id,
            expDate: format(expDate.$d, 'yyyy-MM-dd'),
            skillStatus: status,
            documents: image
        })
        if(response) {
            setIsModalCompetencyOpen(false);
            toast.success('Competency added successfully');
            getCompetencies();
        }
    };

    const handleDelete = async (id: any) => {
        const response = await deleteUserCompetency(state.user?.token, id);
        if(response) {
            getCompetencies();
        } else {
            toast.error('Something went wrong!')
        }
    };

    const competenciesColumns: any = [
        {
            field: "competencyName",
            headerName: "Competency",
            flex: 3,
            renderCell: (params: any) => (
                <div>{params.row.competencyName}</div>
            ),
        },
        {
            field: "expDate",
            headerName: "Expiration Date",
            headerAlign: 'center',
            align: 'center',
            flex: 1,
            cellClassName: (params: any) => {
                const today = new Date();
                const expirationDate = new Date(params.value);
                return expirationDate <= today ? "expired" : "approved";
            },
            renderCell: (params: any) => (
                <div>
                    {format(new Date(params.row.expDate || Date.now()), 'dd/MM/yyyy')}
                </div>
            ),
        },
        {
            field: "status",
            headerName: "Status",
            headerAlign: 'center',
            align: 'center',
            flex: 1,
            renderCell: (params: any) => (
                <div>
                    {getStatus(params.row.status)}
                </div>
            ),
        },
        {
            field: "documents",
            headerName: "Documents",
            headerAlign: 'center',
            align: 'center',
            flex: 1,
            renderCell: (params: any) => 
                <Button
                    variant="outlined"
                    onClick={() => {
                        navigate("/pace-team/resourcing/get-worker-competencies-documents", {
                            state: { userCompetencyId: params.row.userCompetencyId, name: profileInfo?.name, surname: profileInfo?.surname, competencyName: params.row.competencyName
                        }});
                    }}>
                        Documents
                </Button>
        },
        {
            field: "edit",
            headerName: "Edit",
            headerAlign: 'center',
            align: 'center',
            flex: 1,
            renderCell: (params: any) => 
                <Button
                    variant="contained"
                    style={{backgroundColor: 'gray', boxShadow: 'none'}}
                    onClick={() => {
                            setId(params.id);
                            setSelectedCompetency(params.row)
                            setExpDate(dayjs(params.row.expDate))
                            setStatus(params.row.status)
                            setIsModalEditCompetencyOpen(true)
                        }}
                    >
                        Edit
                </Button>
        },
        {
            field: "delete",
            headerName: "Delete",
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: any) => (
                <Button
                    variant="contained"
                    color="error"
                    style={{boxShadow: 'none'}}
                    onClick={() => {
                        setOpen(true);
                        Modal.confirm({
                            title: `Are you sure you want to delete ${params.row.competencyName}?`,
                            onOk: () => handleDelete(params.id),
                            okButtonProps: { danger: true },
                            okText: 'YES',
                            cancelText: 'NO',
                            centered: true
                        });
                    }}
                >
                    Delete
                </Button>
            )
        }
    ];

    const getCompetencies = async () => {
        try {
            setLoading(true);
            const response = await getUserCompetencies(state.user?.token, profileInfo.id);
            setCompetencies(response.data);
            setLoading(false);
        } catch (error) {
            console.log(error);
            setLoading(false);
        }
    }

    useEffect(() => {
        getCompetencies();
    }, [])

    return (
        <>
            <Box display="flex" justifyContent="right" alignItems="center" mb={2}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {setIsModalCompetencyOpen(true)}}
                    style={{ width: 180, boxShadow: "none" }}
                >
                    Add Competency
                </Button>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={2}>
                <h2>Competencies</h2>
                <TextField
                    placeholder="Search..."
                    variant="outlined"
                    size="small"
                    style={{width: 300}}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </Box>
            <Box>
                <DataGrid
                    rows={competencies}
                    columns={competenciesColumns}
                    loading={loading}
                    style={{height: 474}}
                    getRowId={(row) => row.userCompetencyId}
                    sx={{
                        "& .MuiDataGrid-columnHeaderTitle": {
                            color: "#333",
                            fontWeight: "bold",
                            fontSize: 15.5,
                        }
                    }}
                />
            </Box>
            <Modal
                title={`Add Competency for ${profileInfo?.name} ${profileInfo?.surname}`}
                onCancel={handleCancelCompetency}
                open={isModalCompetencyOpen}
                footer={[
                    <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        <Button variant="contained" sx={{ boxShadow: 'none', mb: 2 }} onClick={addCompetency}>
                            Add Competency
                        </Button>
                    </div>
                ]}>
                <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr" }} gap={4} mt={4}>
                    <Autocomplete
                        size="small"
                        options={allCompetencies}
                        onChange={(_, newValue) => {
                            setSelectedCompetency(newValue)
                        }}
                        getOptionLabel={(option: any) => option.competencyName}
                        renderOption={(props, option) => (
                            <ListItem {...props} key={option.competencyName}>xff
                              {option.competencyName}
                            </ListItem>
                        )}
                        renderInput={(params) => (
                            <TextField {...params} label="Competency" placeholder="Select Competency" />
                        )}
                    />
                    <TextField
                        type="file"
                        variant="outlined"
                        fullWidth
                        onChange={handleImageUpload}
                        size="small"
                        inputProps={{
                            multiple: true,
                            accept: ".jpg,.jpeg,.png,.gif,.bmp,.tiff,.xls,.xlsx,.doc,.docx,.pdf"
                        }}
                        sx={{
                            '& input[type="file"]::file-selector-button': {
                                backgroundColor: '#e3e3e3',
                                color: '#000',
                                border: 'none',
                                padding: '4px 12px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                transition: 'background-color 0.3s',
                            },
                        }}
                    />
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DemoContainer components={['DatePicker']}>
                            <DatePicker
                                slotProps={{textField: { size: 'small', fullWidth: true }}}
                                label="Experation Date"
                                value={expDate}
                                format="DD/MM/YYYY"
                                onChange={(value) => {setExpDate(value)}}
                            />
                        </DemoContainer>
                    </LocalizationProvider>
                     <TextField
                        select
                        value={status}
                        id="demo-select-small"
                        size="small"
                        label="Status"
                        sx={{mb: 4}}
                        onChange={(e: any) => {setStatus(e.target.value)}}
                    >
                        <MenuItem value={0}>Pending</MenuItem>
                        <MenuItem value={1}>Approved</MenuItem>
                        <MenuItem value={2}>Denied</MenuItem>
                        <MenuItem value={3}>Not Verified</MenuItem>
                    </TextField>
                </Box>
            </Modal>
            <Modal
                title={`Edit Competency for ${profileInfo?.name} ${profileInfo?.surname}`}
                onCancel={handleCancelEditCompetencyModal}
                open={isModalEditCompetencyOpen}
                footer={[]}
            >
                <EditCompetency
                    id={id}
                    profileInfo={profileInfo}
                    competencies={allCompetencies}
                    selectedCompetency={selectedCompetency}
                    setSelectedCompetency={setSelectedCompetency}
                    expDate={expDate}
                    setExpDate={setExpDate}
                    status={status}
                    setStatus={setStatus}
                    setIsModalEditCompetencyOpen={setIsModalEditCompetencyOpen}
                    getCompetencies={getCompetencies}
                />
            </Modal>
        </>
    )
}

export default Competencies;
